import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Save,
  Loader2,
  ArrowLeft,
  Eye,
  Phone,
  Mail,
  MessageCircle,
  Link as LinkIcon,
  Clock,
  Globe,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GlobalPresetsEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);

  const [config, setConfig] = useState({
    contact: {
      phone: '',
      email: '',
      whatsapp_link: '',
    },
    ctas: {
      primary: { text: '', link: '' },
      secondary: { text: '', link: '' },
    },
    updated_at: null,
  });

  const updateConfig = useCallback((updater) => {
    setConfig(prev => typeof updater === 'function' ? updater(prev) : updater);
    setIsDirty(true);
    isDirtyRef.current = true;
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleNavigateBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate(-1);
  };

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/global-presets-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setIsDirty(false);
        isDirtyRef.current = false;
      }
    } catch (error) {
      toast.error('Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/global-presets-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(config),
      });
      if (res.ok) {
        toast.success('Global presets saved');
        setIsDirty(false);
        isDirtyRef.current = false;
        fetchConfig();
      } else throw new Error();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateContact = (field, value) => {
    updateConfig(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const updateCTA = (type, field, value) => {
    updateConfig(prev => ({
      ...prev,
      ctas: {
        ...prev.ctas,
        [type]: { ...prev.ctas[type], [field]: value }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto p-6" data-testid="global-presets-editor">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-slate-50 py-4 -mx-6 px-6 z-10 border-b">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-900">Global Presets</h1>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Default contact info & CTAs for all pages</span>
              {config.updated_at && (
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" />
                  Updated {new Date(config.updated_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-primary" data-testid="save-global-presets-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </div>

      {/* Contact Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" />
            Global Contact Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500 -mt-2 mb-4">
            These values are used as fallbacks when page-specific contact info is empty.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                Phone Number
              </Label>
              <Input 
                value={config.contact?.phone || ''} 
                onChange={e => updateContact('phone', e.target.value)} 
                placeholder="+268 7600 1234"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                Email
              </Label>
              <Input 
                value={config.contact?.email || ''} 
                onChange={e => updateContact('email', e.target.value)} 
                placeholder="info@eswatiniseals.com"
              />
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-slate-400" />
              WhatsApp Link
            </Label>
            <Input 
              value={config.contact?.whatsapp_link || ''} 
              onChange={e => updateContact('whatsapp_link', e.target.value)} 
              placeholder="https://wa.me/26876001234?text=..."
            />
            <p className="text-xs text-slate-400 mt-1">Full WhatsApp URL with optional pre-filled message</p>
          </div>
        </CardContent>
      </Card>

      {/* Default CTAs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-primary" />
            Default Call-to-Action Buttons
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-slate-500 -mt-2">
            Fallback CTA text and links when page-specific CTAs are not set.
          </p>
          
          {/* Primary CTA */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <h4 className="font-medium text-slate-700">Primary CTA</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input 
                  value={config.ctas?.primary?.text || ''} 
                  onChange={e => updateCTA('primary', 'text', e.target.value)} 
                  placeholder="Get Started"
                />
              </div>
              <div>
                <Label>Link</Label>
                <Input 
                  value={config.ctas?.primary?.link || ''} 
                  onChange={e => updateCTA('primary', 'link', e.target.value)} 
                  placeholder="/#contact"
                />
              </div>
            </div>
          </div>

          {/* Secondary CTA */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <h4 className="font-medium text-slate-700">Secondary CTA</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input 
                  value={config.ctas?.secondary?.text || ''} 
                  onChange={e => updateCTA('secondary', 'text', e.target.value)} 
                  placeholder="WhatsApp Us"
                />
              </div>
              <div>
                <Label>Link</Label>
                <Input 
                  value={config.ctas?.secondary?.link || ''} 
                  onChange={e => updateCTA('secondary', 'link', e.target.value)} 
                  placeholder="https://wa.me/..."
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save */}
      <div className="flex justify-end pb-8">
        <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Global Presets
        </Button>
      </div>
    </div>
  );
};

export default GlobalPresetsEditor;
